import { useTranslation } from 'next-i18next';

import { useFramingSelections } from './hooks/useFramingSelections';
import {
  CurrencyCode,
  FramingProduct,
  FramingSelection,
  FramingSelections,
} from './types';
import { FramingDrawer } from './ui/FramingDrawer';
import {
  FramingSummaryCard,
  FramingSummaryProduct,
} from './ui/FramingSummaryCard';
import { FramingTrigger } from './ui/FramingTrigger';
import {
  getFramePrice,
  parseVariantTitle,
  STANDARD_MOUNTING,
} from './utils/framePricing';

const toSummaryProduct = (product: FramingProduct): FramingSummaryProduct => ({
  id: product.id,
  title: product.title,
  thumbnailSrc: product.thumbnailSrc,
  thumbnailAlt: product.thumbnailAlt,
});

/**
 * Presentational props derived from a frame selection (framed / unframed /
 * pending).
 */
const selectionSummaryProps = (
  selection: FramingSelection,
  framedByDefault: boolean,
) => {
  if (selection === undefined) return { state: 'pending' as const };
  if (selection === null) return { state: 'unframed' as const };

  const { glazing, mounting } = parseVariantTitle(selection.variant.title);

  return {
    state: 'framed' as const,
    frameName: selection.title,
    glazingName: glazing,
    mountingName: mounting === STANDARD_MOUNTING ? undefined : mounting,
    price: getFramePrice(selection),
    included: framedByDefault,
  };
};

export interface FramingSectionProps {
  products: FramingProduct[];
  currencyCode?: CurrencyCode;
  /**
   * Flag required-but-missing selections in red (e.g. after a failed attempt to
   * continue). Purely presentational; the parent decides when to raise it.
   */
  showErrors?: boolean;
  /** Emitted whenever the user changes a framing selection. */
  onSelectionChange?: (selections: FramingSelections) => void;
}

/**
 * "Choose your framing option" section for the full-screen checkout. Renders a
 * framing row per product (empty trigger, or a summary once chosen) and owns the
 * framing drawer. Self-contained: give it products, get selections back.
 */
export const FramingSection = ({
  currencyCode,
  onSelectionChange,
  products,
  showErrors = false,
}: FramingSectionProps) => {
  const { t } = useTranslation('checkout');
  const {
    close,
    confirm,
    isOpen,
    openFor,
    selections,
    targetIds,
    targetProduct,
  } = useFramingSelections({ products, onSelectionChange });

  const isMultiProduct = products.length > 1;
  const isPending = products.every(
    (product) => selections[product.id] === undefined,
  );

  const renderCards = () => {
    // No choice made for any work yet: a single trigger is the entry point,
    // opening the drawer for the whole selection at once.
    if (isPending) {
      return (
        <FramingTrigger
          hasError={showErrors}
          onClick={() => openFor(products.map((product) => product.id))}
        />
      );
    }

    // Every work keeps its own card, even when frames match — works are never
    // grouped. Each card edits its own product's framing.
    return products.map((product) => {
      const selection = selections[product.id];

      return (
        <FramingSummaryCard
          key={product.id}
          currencyCode={currencyCode}
          hasError={showErrors && selection === undefined}
          onEdit={() => openFor([product.id])}
          product={toSummaryProduct(product)}
          {...selectionSummaryProps(selection, product.framedByDefault)}
        />
      );
    });
  };

  return (
    <section className="grid gap-3">
      <h3 className="font-bold">{t('framing.sectionTitle')}</h3>

      <div className="grid gap-3">{renderCards()}</div>

      <FramingDrawer
        currencyCode={currencyCode}
        currentSelection={
          targetProduct ? selections[targetProduct.id] : undefined
        }
        defaultApplyToAll={targetIds.length > 1}
        onClose={close}
        onConfirm={confirm}
        product={targetProduct}
        shown={isOpen}
        showApplyToAll={isMultiProduct}
      />
    </section>
  );
};

export default FramingSection;
